import {useNavigation} from '@react-navigation/native';
import * as React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../redux/selectors/runtime/theme';
import {AcceptorsFilter} from '../../shared/components/acceptorsFilter';
import {AcceptorsFilterForm} from '../../shared/components/acceptorsFilter/model';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Button} from '../../shared/components/button';
import {ContentWithRightImage} from '../../shared/components/contentWithRightImage';
import {Markdown} from '../../shared/components/markdown';
import {ResponsiveView} from '../../shared/components/responsiveView';
import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Text} from '../../shared/components/text';
import {useStyles} from '../../shared/hooks/styles';
import {Content} from '../../shared/models/content';
import {VisImpStatus} from '../../shared/models/visuallyImpairedSettings';
import {Routes, Screens} from '../../shared/routing';
import {useTheme} from '../../shared/theme';
import {DeviceSize, maxDesktopContentWidth, useDeviceSize} from '../../shared/theme/helpers';
import {LoyaltyAcceptorsList} from './components/acceptorsList';
import {Loyality} from '../../assets/images';
import {Image} from '../../shared/components/image';

const imageSizeMultiplier = 1.5;

const routes = [
  {
    title: 'Возможности карты',
    routeName: Screens.CardServices,
    webRoute: Routes.cardService,
  },
  {
    title: 'Программа лояльности',
    routeName: Screens.LoyaltyProgram,
    webRoute: Routes.loyaltyProgram,
  },
];

interface Props {
  content: Content;
  onFilterApply: (formData: AcceptorsFilterForm) => void;
  filter: AcceptorsFilterForm;
}

export default ({content, onFilterApply, filter}: Props) => {
  const theme = useTheme();
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  const themeSettings = useSelector(themeSelector());
  const isVisuallyImpairedSettingActive = themeSettings.visuallyImpaired === VisImpStatus.Active;

  const styles = useStyles(({mediaOnly, mediaExclude}) => {
    return {
      screen: {},
      sectionContainer: {
        flexGrow: 1,
        flexDirection: 'column',
        alignItems: 'center',
        ...mediaOnly(DeviceSize.mobile, {paddingHorizontal: 16}, {}),
        ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 35}, {}),
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 60}, {}),
      },
      contentTextWrapper: mediaOnly(DeviceSize.desktop, {marginBottom: 70}, {marginBottom: 30}),
      contentContainer: {
        width: '100%',
        maxWidth: maxDesktopContentWidth,
        flexDirection: 'column',
        alignItems: 'stretch',
      },
      programSectionContainer: {
        ...mediaOnly(DeviceSize.desktop, {marginTop: 30}, {marginTop: 20}),
        backgroundColor: theme.screen.container[deviceSize].backgroundColor,
      },
      programTitle: {
        ...theme.fonts.h2,
        marginBottom: 30,
        marginTop: 38,
      },
      programContentWithImageContainer: {},
      programContentWithImageIcon: {
        marginTop: -125,
        marginRight: 5,
        width: 397 * imageSizeMultiplier,
        height: 352 * imageSizeMultiplier,
      },
      programContentWithImageBackground: {
        ...mediaOnly(DeviceSize.desktop, {top: -57, right: 54, width: 424, height: 436}, {}),
      },
      programContentWithImageContent: mediaOnly(DeviceSize.mobile, {minHeight: 1}, {minHeight: 450}),
      programDescription: mediaOnly(DeviceSize.desktop, {marginBottom: 40}, {marginBottom: 30}),
      programDescriptionText: mediaOnly(DeviceSize.desktop, theme.fonts.h6Light, theme.fonts.bodySmallLight),
      programFeaturesTitle: mediaOnly(DeviceSize.desktop, {marginBottom: 32}, {marginBottom: 20}),
      programFeaturesTitleText: mediaOnly(DeviceSize.desktop, theme.fonts.h3, theme.fonts.action),
      programFeaturesList: {
        ...mediaOnly(
          DeviceSize.desktop,
          {
            width: 600,
            maxWidth: '100%',
            marginBottom: 60,
          },
          {marginBottom: 20},
        ),
      },
      acceptorsSectionContainer: {
        backgroundColor: theme.color.whiteTextColor,
        ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 25}, {}),
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 50}, {}),
      },
      acceptorsContentContainer: {
        maxWidth: maxDesktopContentWidth + 20,
        overflow: 'hidden',
      },
      acceptorsTitle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
        ...mediaOnly(DeviceSize.desktop, {marginTop: 60, marginBottom: 40}, {marginTop: 30, marginBottom: 25}),
        ...mediaOnly(DeviceSize.mobile, {}, {paddingHorizontal: 10}),
      },
      acceptorsTitleText: {
        ...mediaOnly(DeviceSize.desktop, {...theme.fonts.h2}, {...theme.fonts.h6, width: '100%', textAlign: 'center'}),
        color: theme.color.textColor,
      },
      acceptorsButtonContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        ...mediaOnly(DeviceSize.mobile, {}, {paddingHorizontal: 10}),
      },
      acceptorsFilterButtonMobile: {
        width: '100%',
        marginTop: 10,
        marginBottom: 21,
        marginLeft: 0,
        marginRight: 0,
        ...mediaExclude(DeviceSize.mobile, {maxWidth: 328}),
      },
    };
  });

  return (
    <Screen fullWidth style={styles.screen}>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <View style={[styles.sectionContainer, styles.programSectionContainer]}>
          <View style={styles.contentContainer}>
            <Breadcrumbs routes={routes} />
            <ResponsiveView only={[DeviceSize.desktop]}>
              <Text testID='bonuses-title-id' style={styles.programTitle}>
                Программа лояльности
              </Text>
            </ResponsiveView>
            <ContentWithRightImage
              hideImage={deviceSize !== DeviceSize.desktop}
              containerStyle={styles.programContentWithImageContainer}
              icon={
                <Image source={Loyality as any} width={393 * imageSizeMultiplier} height={357 * imageSizeMultiplier} />
              }
              iconStyle={styles.programContentWithImageIcon}
              backgroundStyle={styles.programContentWithImageBackground}
              contentStyle={styles.programContentWithImageContent}>
              <View style={styles.contentTextWrapper}>
                <Markdown listColumns={deviceSize === DeviceSize.desktop ? 2 : 1}>
                  {content.services.loyaltyProgram.descriptionMarkdown}
                </Markdown>
              </View>
            </ContentWithRightImage>
          </View>
        </View>
        <View style={[styles.sectionContainer, styles.acceptorsSectionContainer]}>
          <View style={[styles.contentContainer, styles.acceptorsContentContainer]}>
            <View style={styles.acceptorsTitle}>
              <Text testID='bonuses-partner-list-id' style={styles.acceptorsTitleText}>
                Партнеры программы лояльности
              </Text>
              {!isVisuallyImpairedSettingActive && deviceSize === DeviceSize.desktop && (
                <AcceptorsFilter onFilterApply={onFilterApply} />
              )}
            </View>
            <ResponsiveView exclude={[DeviceSize.mobile]}>
              {isVisuallyImpairedSettingActive && <AcceptorsFilter visuallyImpaired onFilterApply={onFilterApply} />}
            </ResponsiveView>
            <ResponsiveView exclude={[DeviceSize.desktop]}>
              <View style={styles.acceptorsButtonContainer}>
                <Button
                  id='loyalty-program-category'
                  style={styles.acceptorsFilterButtonMobile}
                  type='secondary'
                  text='Категория'
                  onPress={() => navigation.navigate(Screens.AcceptorsFilter)}
                />
              </View>
            </ResponsiveView>
            <LoyaltyAcceptorsList filter={filter} />
          </View>
        </View>
      </CustomScrollView>
    </Screen>
  );
};
